import { useState, useEffect, useRef } from 'react';

/**
 * BankerAlert - Animated banker horse alert with blue/gold broadcast theme
 * Uses CSS transition for reliable animation (no restarts on re-renders)
 */

export function BankerAlert({ bankerVisible, bankerHorse, showBanker }) {
  const [mounted, setMounted] = useState(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (bankerVisible) {
      // Small delay to trigger CSS transition from scale(0) to scale(1)
      const t = requestAnimationFrame(() => {
        requestAnimationFrame(() => setMounted(true));
      });
      isFirstRender.current = false;
      return () => cancelAnimationFrame(t);
    } else {
      setMounted(false);
      isFirstRender.current = true;
    }
  }, [bankerVisible]);

  if (!bankerVisible || !bankerHorse) return null;

  const isExiting = !showBanker;
  const scale = isExiting ? 'translateX(-50%) scale(0)' : (mounted ? 'translateX(-50%) scale(1)' : 'translateX(-50%) scale(0)');

  return (
    <div
      data-testid="banker-alert"
      style={{
        position: 'absolute',
        bottom: '85px',
        left: '50%',
        transform: scale,
        zIndex: 50,
        transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}
    >
      <div style={{
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#050F23',
        borderRadius: '12px',
        padding: '0',
        minWidth: '315px',
        border: '2px solid var(--theme-panel-border)',
        boxShadow: `0 0 40px rgba(var(--theme-accent-rgb), 0.3), 0 15px 45px rgba(0,0,0,0.5)`,
        textAlign: 'center',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'relative', zIndex: 1, padding: '16px 36px' }}>
          {/* Banner label */}
          <div style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: '0.75rem', fontWeight: 600, color: 'var(--theme-accent)',
            textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '4px',
          }}>
            Banker
          </div>
          {/* Horse number + name */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{
              width: '39px', height: '39px',
              backgroundColor: 'var(--theme-accent)', color: '#0A1628',
              borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 800, fontSize: '1.35rem',
            }}>
              {bankerHorse.number}
            </div>
            <div style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: '1.8rem', fontWeight: 700,
              color: '#FFFFFF', textTransform: 'uppercase',
            }}>
              {bankerHorse.name}
            </div>
          </div>
          {/* Driver */}
          <div style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '10px',
          }}>
            {bankerHorse.driver}
          </div>
          {/* Stats row */}
          <div style={{
            display: 'flex', justifyContent: 'center', gap: '24px',
            borderTop: '1px solid var(--theme-panel-border-subtle)', paddingTop: '8px',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: "'JetBrains Mono'", fontSize: '1.2rem', fontWeight: 700, color: 'var(--theme-accent)' }}>
                {bankerHorse.distribution?.toFixed(1)}%
              </div>
              <div style={{ fontFamily: "'Inter'", fontSize: '0.55rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Innsats</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: "'JetBrains Mono'", fontSize: '1.2rem', fontWeight: 700, color: '#FFFFFF' }}>
                {bankerHorse.odds > 0 ? bankerHorse.odds.toFixed(1) : '-'}
              </div>
              <div style={{ fontFamily: "'Inter'", fontSize: '0.55rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>V-Odds</div>
            </div>
            {bankerHorse.win_percentage > 0 && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: "'JetBrains Mono'", fontSize: '1.2rem', fontWeight: 700, color: '#FFFFFF' }}>
                  {bankerHorse.win_percentage}%
                </div>
                <div style={{ fontFamily: "'Inter'", fontSize: '0.55rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Seier</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
